import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {NavigationStart, Router} from '@angular/router';
import {defer, EMPTY, filter, finalize, Observable, of, switchMap, take, takeUntil} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {map} from 'rxjs';
import {BulkActivityEmailPreviewDialogComponent, BulkEmailPreview} from './bulk-activity-email-preview-dialog.component';

/** Shared by the main Activity list and both related lists on entity forms. */
@Injectable({providedIn: 'root'})
export class BulkActivityEmailSaveService {
  private readonly http = inject(HttpClient);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private pending = false;

  private previewPayload(snapshot: FormData): object {
    const emailDraft: Record<string, any> = {};
    const ids: number[] = [];
    snapshot.forEach((value, key) => {
      if (/^records_ids\[\d+\]$/.test(key) && typeof value === 'string') ids.push(Number(value));
      if (!key.startsWith('data[')) return;
      if (typeof value !== 'string') throw new Error('Bulk email preview cannot include files.');
      const path = [...key.matchAll(/\[([^\]]+)\]/g)].map(match => match[1]);
      let parent = emailDraft;
      for (const part of path.slice(0, -1)) parent = parent[part] ??= {};
      parent[path[path.length - 1]] = value;
    });
    return {
      entity: 'activities', related_entity: 'activities',
      data: {field: 'status', page: 1, bulk_activity_email_preview: true,
        records_ids: ids, email_draft: emailDraft},
    };
  }

  save(data: FormData): Observable<any> {
    return defer(() => {
      if (this.pending) return EMPTY;
      this.pending = true;
      // Freeze the submitted selection and draft while the modal is open.
      const snapshot = new FormData();
      data.forEach((value, key) => snapshot.append(key, value));
      // The gateway's entity/search permission check reads related_entity from JSON.
      // A multipart request fails before reaching the preview service.
      const previewRequest = this.previewPayload(snapshot);
      let modal: MatDialogRef<BulkActivityEmailPreviewDialogComponent> | undefined;
      return this.http.post<{data: BulkEmailPreview}>(environment.baseApi + 'entity/search', previewRequest).pipe(
        switchMap(({data: preview}) => {
          if (!preview.review_required && preview.can_confirm && preview.review_token) return of(preview.review_token);
          modal = this.dialog.open(BulkActivityEmailPreviewDialogComponent, {
            width: '1000px', maxWidth: '96vw', autoFocus: 'first-tabbable', closeOnNavigation: true,
            data: {preview, request: previewRequest},
          });
          return modal.afterClosed().pipe(take(1));
        }),
        map(review => typeof review === 'string' ? {token: review, force: false} : review as {token?: string; force?: boolean} | undefined),
        filter((review): review is {token: string; force: boolean} => typeof review?.token === 'string' && review.token.length > 0),
        switchMap(review => {
          snapshot.set('bulk_email_preview_token', review.token);
          if (review.force) snapshot.set('force_send_confirmed', '1');
          return this.http.post(environment.baseApi + 'entity/bulk-update', snapshot);
        }),
        takeUntil(this.router.events.pipe(filter(event => event instanceof NavigationStart))),
        finalize(() => { modal?.close(); this.pending = false; }),
      );
    });
  }
}
